import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, ArrowUpRight } from "lucide-react";
import { useContent } from "@/lib/content";
import { MaskedLines, FadeUp, Eyebrow } from "@/components/Motion";
import Marquee from "@/components/Marquee";
import Logo from "@/components/Logo";
import { imgSrc } from "@/lib/api";

function Spotlight({ children, className = "" }) {
  const ref = useRef(null);
  return (
    <div
      ref={ref}
      className={`spotlight-card ${className}`}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect();
        ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
        ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
      }}
    >
      {children}
    </div>
  );
}

export default function Home() {
  const content = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  if (!content) return <div className="min-h-screen bg-ivory" />;
  const hero = content.hero;

  return (
    <main>
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden pb-16 pt-28 lg:pt-24" data-testid="home-hero">
        <div className="wrap grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="relative z-10 lg:col-span-7">
            <Eyebrow className="mb-6">{hero.eyebrow}</Eyebrow>
            <h1 className="font-serif text-[11vw] leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              <MaskedLines
                baseDelay={0.15}
                lines={[
                  <span key="a">Built with <em className="font-medium italic text-gold">purpose</em>.</span>,
                  <span key="b">Finished with <em className="font-medium italic text-gold">precision</em>.</span>,
                ]}
              />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-md text-base leading-relaxed text-charcoal/65 sm:text-lg"
            >
              {hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <Link to="/contact" className="btn-dark" data-testid="hero-request-quote-button">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <a href="tel:+61456492849" className="btn-ghost" data-testid="hero-phone-button">
                <Phone size={15} /> 0456 492 849
              </a>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: imgY }} className="relative mx-auto max-w-[420px] lg:max-w-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="photo-frame aspect-[4/5] rounded-t-[999px] rounded-b-lg shadow-2xl shadow-charcoal/20"
                data-testid="hero-image"
              >
                <img src={imgSrc(hero.image)} alt="Finished timber verandah and deck by Heritage Constructions" />
              </motion.div>            </motion.div>
          </div>
        </div>

        <motion.div style={{ y: bgY }} className="pointer-events-none absolute -left-24 top-1/3 hidden h-[480px] w-[480px] rounded-full border border-gold/15 lg:block" />
        <div className="pointer-events-none absolute bottom-10 left-5 hidden items-center gap-3 sm:flex">
          <span className="h-px w-10 bg-charcoal/30" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal/40">Scroll</span>
        </div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <Marquee items={content.marquee} />

      {/* ---------- PHILOSOPHY ---------- */}
      <section className="py-20 sm:py-28 lg:py-36" data-testid="home-philosophy">
        <div className="wrap grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <FadeUp>
              <div className="photo-frame aspect-[4/3] rounded-lg lg:aspect-[4/5]" data-testid="philosophy-image">
                <img src={imgSrc(content.philosophy.image)} alt="Timber wall framing under construction" />
              </div>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Eyebrow className="mb-6">{content.philosophy.eyebrow}</Eyebrow>
            <FadeUp delay={0.05}>
              <h2 className="font-serif text-3xl leading-tight text-charcoal sm:text-4xl lg:text-5xl">
                {content.philosophy.title}
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-7 max-w-lg leading-relaxed text-charcoal/70">{content.philosophy.body}</p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mt-8 font-serif text-xl italic text-timber">{content.philosophy.signature}</p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <Link to="/about" className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal" data-testid="philosophy-about-link">
                <span className="border-b border-gold pb-1 transition-colors group-hover:border-charcoal">Our story</span>
                <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------- SERVICES BENTO ---------- */}
      <section className="bg-stone/40 py-20 sm:py-28 lg:py-32" data-testid="home-services">
        <div className="wrap">
          <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <FadeUp delay={0.05}>
                <h2 className="max-w-xl font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">
                  Carpentry For Every Part Of Your Home
                </h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.1}>
              <Link to="/services" className="btn-ghost" data-testid="home-all-services-link">All services <ArrowRight size={15} /></Link>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
            {(content.services || []).slice(0, 4).map((s, i) => (
              <FadeUp
                key={s.id}
                delay={i * 0.06}
                className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : i === 2 ? "md:col-span-5" : "md:col-span-7"}
              >
                <Spotlight className="group h-full">
                  <Link to="/services" className="flex h-full flex-col overflow-hidden rounded-lg border border-charcoal/10 bg-paper transition-shadow duration-500 hover:shadow-2xl hover:shadow-charcoal/10">
                    <div className="photo-frame aspect-[3/2] w-full rounded-none">
                      <img src={imgSrc(s.image)} alt={s.title} loading="lazy" />
                    </div>
                    <div className="flex flex-1 items-start justify-between gap-6 p-7 sm:p-8">
                      <div>
                        <h3 className="mt-2 font-serif text-2xl text-charcoal sm:text-3xl">{s.title}</h3>
                        <p className="mt-2 max-w-sm text-sm leading-relaxed text-charcoal/60">{s.tagline}</p>
                      </div>
                      <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-charcoal/15 transition-all duration-300 group-hover:border-gold group-hover:bg-gold">
                        <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </Link>
                </Spotlight>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- SERVICE AREA ---------- */}
      <section className="border-y border-charcoal/10 bg-stone/40 py-20 sm:py-24" data-testid="home-areas">
        <div className="wrap flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="lg:w-[36rem] lg:shrink-0">
            <Eyebrow className="mb-5">Where we work</Eyebrow>
            <FadeUp delay={0.05}>
              <h2 className="max-w-xl font-serif text-4xl leading-[1.05] text-charcoal sm:text-5xl lg:text-6xl">
                Established in the Yarra Valley. Building across the surrounding areas.
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.1} className="flex flex-wrap gap-2.5">
            {(content.about?.areas || []).map((a) => (
              <span key={a} className="rounded-full border border-charcoal/15 bg-paper px-4 py-2 text-xs text-charcoal/70">{a}</span>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="py-20 sm:py-28" data-testid="home-cta">
        <div className="wrap">
          <div className="relative overflow-hidden rounded-lg border border-charcoal/10 bg-[#F3EDE0] px-7 py-16 text-center text-charcoal sm:px-12 sm:py-24">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full border border-gold/30" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full border border-gold/30" />
            <FadeUp className="mb-8 flex justify-center">
              <Logo size="h-24" wide />
            </FadeUp>
            <Eyebrow className="mb-6">Request a Quote</Eyebrow>
            <FadeUp delay={0.05}>
              <h2 className="mx-auto max-w-2xl font-serif text-4xl leading-tight text-charcoal sm:text-5xl lg:text-6xl">
                Have a project in mind for your home?
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-charcoal/65 sm:text-base">
                Inquire today. You'll get honest advice, a detailed quote and a build you'll be proud of.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <Link to="/contact" className="btn-gold" data-testid="home-cta-quote-button">Request a Quote <ArrowRight size={16} /></Link>
                <a href="tel:+61456492849" className="btn-ghost" data-testid="home-cta-phone-button">Call 0456 492 849</a>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

    </main>
  );
}
